/* Kirk Cameron theme: small progressive enhancements only.
   1. Mobile menu (native <dialog>: focus stays inside, Escape closes, focus returns to the button).
   2. Project library filter (a native select, URL state, empty state).
   3. Video posters: the provider's player loads only when the visitor presses play.
   4. Hero reel: paused on its poster for visitors who prefer reduced motion.
   5. Sticky header: compacts after the page scrolls; "Show on scroll up" hides it on the way down.
   Each has a no-JavaScript fallback in the markup and CSS: the menu links show inline, every
   project is listed, the play control is a plain link to the video, the reel is a plain
   muted autoplay video, and a pinned header simply stays pinned at full height. */
(function () {
  'use strict';

  function initMenu(scope) {
    scope.querySelectorAll('[data-kc-header]').forEach(function (header) {
      if (header.dataset.kcReady) return;
      header.dataset.kcReady = 'true';
      var dialog = header.querySelector('[data-kc-menu]');
      var opener = header.querySelector('[data-kc-menu-open]');
      if (!dialog || !opener || typeof dialog.showModal !== 'function') return;

      opener.addEventListener('click', function () {
        dialog.showModal();
      });
      dialog.querySelectorAll('[data-kc-menu-close]').forEach(function (button) {
        button.addEventListener('click', function () {
          dialog.close();
        });
      });
      // A click on the backdrop lands on the dialog element itself.
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', function () {
        opener.focus();
      });
      // Close when the viewport grows into the desktop layout.
      var desktop = window.matchMedia('(min-width: 900px)');
      var onChange = function (event) {
        if (event.matches && dialog.open) dialog.close();
      };
      if (desktop.addEventListener) desktop.addEventListener('change', onChange);
    });
  }

  function initLibrary(scope) {
    scope.querySelectorAll('[data-kc-library]').forEach(function (library) {
      if (library.dataset.kcReady) return;
      var bar = library.querySelector('[data-kc-filters]');
      var cards = Array.prototype.slice.call(library.querySelectorAll('[data-kc-project]'));
      if (!bar || !cards.length) return;
      library.dataset.kcReady = 'true';

      var select = bar.querySelector('[data-kc-filter-select]');
      var count = bar.querySelector('[data-kc-count]');
      var empty = library.querySelector('[data-kc-empty]');
      var grid = library.querySelector('[data-kc-cards]');
      var reset = library.querySelector('[data-kc-filter-reset]');
      if (!select) return;

      // "From the archive" matches by status across every category; the others match by category.
      function matches(card, value) {
        if (value === 'all') return true;
        if (value === 'archive') return card.dataset.status === 'archive';
        return card.dataset.category === value;
      }

      // Only offer a choice that has at least one project behind it. Options are removed rather
      // than hidden: iOS Safari's picker ignores the hidden attribute on an option.
      Array.prototype.slice.call(select.options).forEach(function (option) {
        if (option.value === 'all') return;
        var has = cards.some(function (card) { return matches(card, option.value); });
        if (!has) select.removeChild(option);
      });
      var known = Array.prototype.slice.call(select.options).map(function (option) { return option.value; });

      function apply(value, updateUrl) {
        if (known.indexOf(value) === -1) value = 'all';
        var shown = 0;
        cards.forEach(function (card) {
          var match = matches(card, value);
          card.hidden = !match;
          if (match) shown += 1;
        });
        if (select.value !== value) select.value = value;
        if (count && count.dataset.template) {
          count.textContent = count.dataset.template.replace('[count]', shown).replace('[total]', cards.length);
        }
        if (empty) empty.hidden = shown !== 0;
        if (grid) grid.hidden = shown === 0;
        if (updateUrl && window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          if (value === 'all') url.searchParams.delete('type');
          else url.searchParams.set('type', value);
          window.history.replaceState({}, '', url);
        }
      }

      select.addEventListener('change', function () {
        apply(select.value, true);
      });
      if (reset) {
        reset.addEventListener('click', function () {
          apply('all', true);
          select.focus();
        });
      }

      bar.hidden = false;
      var initial = new URLSearchParams(window.location.search).get('type') || 'all';
      apply(initial, false);
    });
  }

  function initVideo(scope) {
    scope.querySelectorAll('[data-kc-video]').forEach(function (frame) {
      if (frame.dataset.kcReady) return;
      frame.dataset.kcReady = 'true';
      var link = frame.querySelector('[data-kc-video-play]');
      var embed = frame.dataset.embed;

      // YouTube answers a missing large thumbnail with a tiny placeholder. Swap to the standard one.
      var poster = frame.querySelector('img[data-fallback]');
      if (poster) {
        var useFallback = function () {
          if (!poster.dataset.fallback) return;
          poster.src = poster.dataset.fallback;
          poster.removeAttribute('data-fallback');
        };
        var check = function () {
          if (poster.naturalWidth > 0 && poster.naturalWidth <= 120) useFallback();
        };
        if (poster.complete) check();
        poster.addEventListener('load', check);
        poster.addEventListener('error', useFallback);
      }

      if (!link || !embed) return;
      link.addEventListener('click', function (event) {
        // Leave modified clicks alone so the link can still open the provider in a new tab.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        var player = document.createElement('iframe');
        player.src = embed;
        player.title = frame.dataset.title || '';
        player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        player.allowFullscreen = true;
        player.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        while (frame.firstChild) frame.removeChild(frame.firstChild);
        frame.appendChild(player);
        player.focus();
      });
    });
  }

  function initReel(scope) {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    scope.querySelectorAll('[data-kc-reel]').forEach(function (video) {
      if (video.dataset.kcReady) return;
      video.dataset.kcReady = 'true';
      var sync = function () {
        if (reduce.matches) {
          video.pause();
          video.removeAttribute('autoplay');
        } else if (video.paused) {
          var played = video.play();
          if (played && played.catch) played.catch(function () {});
        }
      };
      sync();
      if (reduce.addEventListener) reduce.addEventListener('change', sync);
    });
  }

  // One window listener for the life of the page. It looks the header up on each tick, so the
  // theme editor can re-render the section without leaving a stale listener behind.
  var stickyReady = false;
  function initSticky() {
    if (stickyReady) return;
    stickyReady = true;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var lastY = window.pageYOffset;
    var queued = false;
    // After an anchor jump the page may scroll smoothly for a moment; keep the header shown
    // through that scroll rather than hiding it again on the way down.
    var holdUntil = 0;

    function wrapperOf(bar) {
      return bar.closest('.shopify-section') || bar.parentElement;
    }

    function update() {
      queued = false;
      var bar = document.querySelector('[data-kc-sticky]');
      if (!bar) return;
      var wrapper = wrapperOf(bar);
      var mode = bar.dataset.kcSticky;
      var height = bar.offsetHeight || 72;
      var y = Math.max(0, window.pageYOffset);
      wrapper.classList.toggle('is-compact', y > height);

      var hidden = wrapper.classList.contains('is-hidden');
      if (mode === 'scroll' && !reduce.matches) {
        var dialog = bar.querySelector('[data-kc-menu]');
        var menuOpen = Boolean(dialog && dialog.open);
        if (y <= height || menuOpen || y < lastY || Date.now() < holdUntil) {
          hidden = false;
        } else if (y - lastY > 4 && y > height * 2) {
          hidden = true;
        }
      } else {
        hidden = false;
      }
      wrapper.classList.toggle('is-hidden', hidden);
      lastY = y;
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // A jump to an anchor is navigation, not reading: bring the header back with it.
    window.addEventListener('hashchange', function () { lastY = Infinity; holdUntil = Date.now() + 1500; schedule(); });
    if (reduce.addEventListener) reduce.addEventListener('change', schedule);
    // Theme editor: show the header when the merchant selects it.
    document.addEventListener('shopify:section:select', function (event) {
      var bar = event.target.querySelector('[data-kc-sticky]');
      if (bar) wrapperOf(bar).classList.remove('is-hidden');
    });
    update();
  }

  function init(scope) {
    initMenu(scope);
    initLibrary(scope);
    initVideo(scope);
    initReel(scope);
    initSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: sections are re-rendered in place, so set them up again.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
